import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass, Question, Tray } from "@phosphor-icons/react";
import {
  DropdownMenuTrigger,
  OrgSwitcher,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarIconButton,
  SidebarItem,
} from "@hulahoop/ui";
import { ShellOrganizationMark } from "./ShellOrganizationMark";
import { ShellQuickCreate } from "./ShellQuickCreate";
import { SHELL_NAVIGATION, isShellNavigationActive } from "./shellNavigation";

interface ShellSidebarRailProps {
  organizationName: string;
  organizationLogoUrl?: string | null;
  organizationLabel: string;
  menu: ReactNode;
  switching: boolean;
  inboxOpen: boolean;
  onSearch: () => void;
  onInbox: () => void;
  onHelp: () => void;
}

export function ShellSidebarRail({
  organizationName,
  organizationLogoUrl,
  organizationLabel,
  menu,
  switching,
  inboxOpen,
  onSearch,
  onInbox,
  onHelp,
}: ShellSidebarRailProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  return (
    <Sidebar rail>
      <SidebarHeader className="items-center gap-1.5 border-b-0 px-0 py-0 pb-1">
        <span className="flex min-h-(--control-h-xl) items-center">
          <OrgSwitcher>
            <SidebarIconButton
              aria-label={organizationLabel}
              className="size-(--control-h-sm)"
              render={<DropdownMenuTrigger />}
              disabled={switching}
              nativeButton
            >
              <ShellOrganizationMark name={organizationName} logoUrl={organizationLogoUrl} />
            </SidebarIconButton>
            {menu}
          </OrgSwitcher>
        </span>
        <ShellQuickCreate />
        <SidebarIconButton
          aria-label={t("shell.search")}
          tooltip={t("shell.search")}
          className="size-(--control-h-sm)"
          onClick={onSearch}
        >
          <MagnifyingGlass />
        </SidebarIconButton>
      </SidebarHeader>
      <SidebarContent className="items-center px-0">
        <SidebarGroup className="items-center">
          {SHELL_NAVIGATION.map(({ path, label, icon: Icon, group }) =>
            group === "" ? (
              <SidebarItem
                key={path}
                render={<Link to={path} />}
                icon={Icon}
                aria-label={t(label)}
                active={isShellNavigationActive(path, pathname)}
              />
            ) : null,
          )}
          <SidebarItem
            render={<button type="button" />}
            icon={Tray}
            aria-label={t("shell.inbox")}
            aria-expanded={inboxOpen}
            aria-current={false}
            active={inboxOpen}
            onClick={onInbox}
          />
          {SHELL_NAVIGATION.map(({ path, label, icon: Icon, group }) =>
            group === "" ? null : (
              <SidebarItem
                key={path}
                render={<Link to={path} />}
                icon={Icon}
                aria-label={t(label)}
                active={isShellNavigationActive(path, pathname)}
              />
            ),
          )}
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="items-center border-t-0 px-0">
        <SidebarItem
          render={<button type="button" />}
          icon={Question}
          aria-label={t("shell.help")}
          aria-current={false}
          onClick={onHelp}
        />
      </SidebarFooter>
    </Sidebar>
  );
}
